import { useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { ApiError, createApi } from "../api/client";
import { ErrorText, Field, PrimaryButton } from "./_auth_ui";

export function Contact() {
  const { email: signedInEmail, getIdToken } = useAuth();
  const api = createApi(getIdToken);

  const [name, setName] = useState("");
  const [email, setEmail] = useState(signedInEmail ?? "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejection, setRejection] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setRejection(null);
    try {
      await api.submitContact({ name, email, subject: subject || undefined, message, website });
      setSent(true);
    } catch (err: unknown) {
      if (err instanceof ApiError && err.code === "content_rejected") {
        setRejection(err.message);
      } else {
        setError(err instanceof Error ? err.message : "Something went wrong");
      }
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <section className="mx-auto max-w-2xl px-6 py-16 text-center">
        <h1 className="font-serif text-4xl tracking-tight">Thank you.</h1>
        <p className="mt-3 text-neutral-600">
          Your message is on its way. We'll reply to {email} within a day or two.
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-2xl px-6 py-12">
      <h1 className="font-serif text-4xl tracking-tight">Customer service request</h1>
      <p className="mt-3 text-neutral-600 max-w-lg">
        Something bigger in mind? A wedding reel, a memorial slideshow, multiple
        photographs stitched into one film — tell us what you need and we'll come
        back with a quote.
      </p>

      {rejection && (
        <div role="alert" className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <p className="font-serif text-lg text-neutral-900">We can't send this one.</p>
          <p className="mt-1 text-sm text-neutral-700">{rejection}</p>
          <p className="mt-3 text-sm text-neutral-600">Please rephrase and try again.</p>
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <Field label="Your name" value={name} onChange={setName} required autoComplete="name" />
        <Field
          label="Email"
          value={email}
          onChange={setEmail}
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
        />
        <Field
          label="Subject (optional)"
          value={subject}
          onChange={setSubject}
          placeholder="e.g. Wedding anniversary reel"
        />

        <label className="block">
          <span className="block text-sm text-neutral-700">What would you like us to make?</span>
          <textarea
            className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 transition min-h-[160px] resize-y"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={4000}
            required
            placeholder="Tell us about the photos, the occasion, any deadlines…"
          />
          <span className="mt-1 block text-xs text-neutral-400 text-right">
            {message.length}/4000
          </span>
        </label>

        {/* Honeypot: hidden from users, attracts bots */}
        <label aria-hidden className="hidden">
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>

        <ErrorText error={error} />

        <PrimaryButton loading={busy} disabled={!name || !email || !message}>
          Send request
        </PrimaryButton>
      </form>
    </section>
  );
}
